import { useUser } from "@/hooks/useUser";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { HiUser } from "react-icons/hi2";
import { MdArrowOutward } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";

// Components
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ToggleLanguage from "./ToggleLanguage";
import { Button } from "./ui/button";

// Config
import { web } from "@/config/System";
import useTimePeriode from "@/hooks/useTimePeriode";
import { AnimatedThemeToggler } from "./ui/animated-theme-toggler";
import {
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  NavBody,
  NavItemLink,
  NavItems,
  Navbar as ResizableNavbar,
} from "./ui/resizable-navbar";

// --- Data Menu ---
const menusLandingPage = [
  { name: "Jalur Seleksi", path: "#jalur-seleksi" },
  { name: "Jadwal", path: "#informasi-terkini" },
  { name: "Panduan", path: "#daftar-unduhan" },
  // { name: "Daftar Unduhan", path: "#daftar-unduhan" },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isPending, logoutUser } = useUser();
  const { pathname } = useLocation();
  const { dataPeriode } = useTimePeriode();
  const isJalurInternasional =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "internasional";
  const isJalurStudentexchange =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "studentexchange";

  const showNavLink = !pathname.startsWith("/informasi-jalur");

  const userRoles = user?.jenis_user || [];
  const siswa = userRoles.includes("siswa");
  const isMultiRole = user ? userRoles?.length > 0 && !siswa : false;
  const currentMenus = pathname === "/" ? menusLandingPage : [];

  const isDashboard = pathname.startsWith("/dashboard");
  const showToggleLanguage =
    isDashboard ||
    (!!dataPeriode && (isJalurInternasional || isJalurStudentexchange));

  return (
    <ResizableNavbar className="top-5">
      {/* --- DESKTOP VIEW --- */}
      <NavBody>
        {/* LEFT: LOGO */}
        <Link to={"/"} className="flex gap-2 items-center min-w-fit z-50">
          <img src={web.logo} alt={web.univAcronym} width={32} />
          <div className="hidden lg:block">
            <h1 className="font-bold text-sm leading-tight">{web.title}</h1>
            <p className="text-[10px] uppercase text-muted-foreground font-semibold tracking-wider">
              {web.univAcronym}
            </p>
          </div>
        </Link>

        {/* CENTER: MENU ITEMS */}
        {showNavLink && (
          <>
            <NavItems className="hidden lg:flex">
              {/* Dashboard Siswa Link */}
              {siswa && (
                <NavItemLink
                  active={pathname.startsWith("/dashboard")}
                  href="/dashboard"
                >
                  Dashboard
                </NavItemLink>
              )}

              {/* Landing Page Links */}
              {currentMenus.map((menu, index) => (
                <NavItemLink
                  key={index}
                  active={pathname.startsWith(menu.path)}
                  href={menu.path}
                >
                  {menu.name}
                </NavItemLink>
              ))}

              {/* External Link PMB */}
              <a
                href={web.linkPMB}
                target="_blank"
                className="relative px-4 py-2 text-sm font-medium text-neutral-600 hover:text-purple-700 transition-colors flex items-center gap-1 group"
              >
                Info PMB
                <MdArrowOutward className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 duration-200" />
              </a>
            </NavItems>
            <AnimatedThemeToggler className="mr-3" />
          </>
        )}

        {/* RIGHT: ACTIONS (Language + Auth) */}
        <div className="flex items-center gap-3 min-w-fit justify-end z-50">
          {pathname !== "/" && showNavLink && showToggleLanguage && (
            <ToggleLanguage />
          )}

          {isPending ? (
            <Button variant="ghost" size="icon" className="rounded-full">
              <Loader2 className="h-4 w-4 animate-spin" />
            </Button>
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full pl-2 pr-4 gap-2 border-zinc-200/50"
                >
                  <div className="w-6 h-6 bg-zinc-100 rounded-full flex items-center justify-center">
                    <HiUser className="text-zinc-600" />
                  </div>
                  <span className="text-sm font-medium max-w-[100px] truncate">
                    {user?.nama_lengkap?.split(" ")[0]}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 mt-2">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isMultiRole && (
                  <Link to="/global/dashboard">
                    <DropdownMenuItem>Global Dashboard</DropdownMenuItem>
                  </Link>
                )}
                {siswa && (
                  <Link to="/profile">
                    <DropdownMenuItem>Profile</DropdownMenuItem>
                  </Link>
                )}
                <DropdownMenuItem
                  onClick={logoutUser}
                  className="text-red-600 focus:text-red-600"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex gap-2">
              <Link to="/login">
                <Button variant={"default"}>Login</Button>
              </Link>
              <Link to="/registrasi">
                <Button variant={"outline"}>Buat Akun</Button>
              </Link>
            </div>
          )}
        </div>
      </NavBody>

      {/* --- MOBILE VIEW --- */}
      <MobileNav>
        <MobileNavHeader>
          <Link to={"/"} className="flex gap-2 items-center">
            <img src={web.logo} alt={web.univAcronym} width={30} />
            <h1 className="font-bold text-sm">{web.univAcronym}</h1>
          </Link>
          <MobileNavToggle
            isOpen={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
        </MobileNavHeader>

        <MobileNavMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        >
          <div className="flex flex-col gap-4 w-full">
            {pathname !== "/" && showNavLink && showToggleLanguage && (
              <div className="px-4">
                <ToggleLanguage />
              </div>
            )}

            {/* Mobile Links */}
            <div className="flex flex-col space-y-1">
              {siswa && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-200 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 rounded-md transition-colors"
                >
                  Dashboard Siswa
                </Link>
              )}
              {currentMenus.map((menu, idx) => (
                <a
                  key={idx}
                  href={menu.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-black dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800/50 rounded-md transition-colors"
                >
                  {menu.name}
                </a>
              ))}
              <a
                href={web.linkPMB}
                target="_blank"
                className="px-4 py-3 text-sm font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors flex items-center justify-between"
              >
                Info PMB <MdArrowOutward />
              </a>
            </div>

            <div className="h-px bg-zinc-100 w-full my-1" />

            {/* Mobile Auth */}
            <div className="px-4 flex flex-col gap-2 pb-4">
              <AnimatedThemeToggler />
              {user ? (
                <>
                  <div className="flex items-center gap-3 py-2">
                    <div className="w-8 h-8 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center">
                      <HiUser />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {user.nama_lengkap}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {userRoles[0]}
                      </span>
                    </div>
                  </div>
                  {isMultiRole && (
                    <Link to="/global/dashboard">
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                      >
                        Global Dashboard
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="destructive"
                    onClick={logoutUser}
                    className="w-full"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  <Link to="/login" className="w-full">
                    <Button className="w-full">Login</Button>
                  </Link>
                  <Link to="/registrasi" className="w-full">
                    <Button variant="outline" className="w-full">
                      Buat Akun
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </MobileNavMenu>
      </MobileNav>
    </ResizableNavbar>
  );
}
